import { useCallback } from 'react';
import { logDailyActivity } from '../utils/studyStreakManager';
import { saveUserActivity } from '../firebase/subcollections';

// Activity types for consistent logging
export const ACTIVITY_TYPES = {
  // Daily activities
  DAILY_LOGIN: 'daily_login',
  DASHBOARD_VISIT: 'dashboard_visit',
  
  // Test activities
  TEST_STARTED: 'test_started',
  TEST_COMPLETED: 'test_completed',
  TEST_PAUSED: 'test_paused',
  TEST_RESUMED: 'test_resumed',
  
  // Study activities
  COURSE_ENROLLED: 'course_enrolled',
  LESSON_COMPLETED: 'lesson_completed',
  PRACTICE_SESSION: 'practice_session',
  REVISION_STARTED: 'revision_started',
  
  // Content activities
  NOTE_CREATED: 'note_created',
  NOTE_UPDATED: 'note_updated',
  DOUBT_POSTED: 'doubt_posted',
  
  // Social activities
  COMMENT_POSTED: 'comment_posted',
  RATING_GIVEN: 'rating_given',
  SHARE_ACTION: 'share_action',
  
  // System activities
  PROFILE_UPDATED: 'profile_updated',
  SETTINGS_CHANGED: 'settings_changed',
  MANUAL_REFRESH: 'manual_refresh'
};

export const useActivityLogger = (userId) => {
  // Log activity for streak tracking
  const logActivity = useCallback(async (activityType, additionalData = {}) => {
    if (!userId) return;
    
    try {
      // Log for streak tracking
      await logDailyActivity(userId, activityType);
      
      // Log detailed activity for analytics
      await saveUserActivity(userId, {
        type: activityType,
        title: getActivityTitle(activityType),
        description: getActivityDescription(activityType, additionalData),
        metadata: {
          ...additionalData,
          timestamp: new Date().toISOString()
        }
      });
      
      // Activity logged
    } catch (error) {
      // Error logging activity
    }
  }, [userId]);

  // Convenience methods for common activities
  const logTestActivity = useCallback((testId, testName, score = null) => {
    return logActivity(ACTIVITY_TYPES.TEST_COMPLETED, {
      testId,
      testName,
      score,
      category: 'test'
    });
  }, [logActivity]);

  const logCourseActivity = useCallback((courseId, courseName, action = 'enrolled') => {
    return logActivity(ACTIVITY_TYPES.COURSE_ENROLLED, {
      courseId,
      courseName,
      action,
      category: 'course'
    });
  }, [logActivity]);

  const logStudyActivity = useCallback((topic, duration = null) => {
    return logActivity(ACTIVITY_TYPES.PRACTICE_SESSION, {
      topic,
      duration,
      category: 'study'
    });
  }, [logActivity]);

  const logContentActivity = useCallback((contentType, contentId, contentTitle) => {
    const activityType = getContentActivityType(contentType);
    return logActivity(activityType, {
      contentId,
      contentTitle,
      contentType,
      category: 'content'
    });
  }, [logActivity]);

  return {
    logActivity,
    logTestActivity,
    logCourseActivity,
    logStudyActivity,
    logContentActivity,
    ACTIVITY_TYPES
  };
};

// Helper functions
const getActivityTitle = (activityType) => {
  const titles = {
    [ACTIVITY_TYPES.DAILY_LOGIN]: 'Daily Login',
    [ACTIVITY_TYPES.DASHBOARD_VISIT]: 'Dashboard Visit',
    [ACTIVITY_TYPES.TEST_STARTED]: 'Test Started',
    [ACTIVITY_TYPES.TEST_COMPLETED]: 'Test Completed',
    [ACTIVITY_TYPES.TEST_PAUSED]: 'Test Paused',
    [ACTIVITY_TYPES.TEST_RESUMED]: 'Test Resumed',
    [ACTIVITY_TYPES.COURSE_ENROLLED]: 'Course Enrolled',
    [ACTIVITY_TYPES.LESSON_COMPLETED]: 'Lesson Completed',
    [ACTIVITY_TYPES.PRACTICE_SESSION]: 'Practice Session',
    [ACTIVITY_TYPES.REVISION_STARTED]: 'Revision Started',
    [ACTIVITY_TYPES.NOTE_CREATED]: 'Note Created',
    [ACTIVITY_TYPES.NOTE_UPDATED]: 'Note Updated',
    [ACTIVITY_TYPES.DOUBT_POSTED]: 'Doubt Posted',
    [ACTIVITY_TYPES.COMMENT_POSTED]: 'Comment Posted',
    [ACTIVITY_TYPES.RATING_GIVEN]: 'Rating Given',
    [ACTIVITY_TYPES.SHARE_ACTION]: 'Content Shared',
    [ACTIVITY_TYPES.PROFILE_UPDATED]: 'Profile Updated',
    [ACTIVITY_TYPES.SETTINGS_CHANGED]: 'Settings Changed',
    [ACTIVITY_TYPES.MANUAL_REFRESH]: 'Manual Refresh'
  };
  
  return titles[activityType] || 'Activity';
};

const getActivityDescription = (activityType, data) => {
  switch (activityType) {
    case ACTIVITY_TYPES.TEST_COMPLETED:
      return `Completed ${data.testName} with ${data.score}% score`;
    case ACTIVITY_TYPES.COURSE_ENROLLED:
      return `Enrolled in ${data.courseName}`;
    case ACTIVITY_TYPES.PRACTICE_SESSION:
      return `Studied ${data.topic} for ${data.duration || 'some time'}`;
    case ACTIVITY_TYPES.NOTE_CREATED:
      return `Created note: ${data.contentTitle}`;
    case ACTIVITY_TYPES.DOUBT_POSTED:
      return `Posted doubt: ${data.contentTitle}`;
    default:
      return getActivityTitle(activityType);
  }
};

const getContentActivityType = (contentType) => {
  switch (contentType) {
    case 'note':
      return ACTIVITY_TYPES.NOTE_CREATED;
    case 'doubt':
      return ACTIVITY_TYPES.DOUBT_POSTED;
    case 'comment':
      return ACTIVITY_TYPES.COMMENT_POSTED;
    default:
      return ACTIVITY_TYPES.MANUAL_REFRESH;
  }
};
